import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import { useAuth } from '../../stores/AuthContext';
import './CreateIngestion.css';

interface TestResponse {
  message: string;
  status: 'success' | 'error';
  timestamp?: string;
  test_info?: {
    user: string;
    connection_test: string;
    metadata_extraction: string;
  };
}

const CreateIngestion: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [testResult, setTestResult] = useState<TestResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleTestIngestion = async () => {
    setIsLoading(true);
    setTestResult(null);

    try {
      const response = await fetch(`${API_BASE_URL}/ingestion/test/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        setTestResult({
          ...data,
          status: 'success',
        });
      } else {
        setTestResult({
          message: data.message || 'Test failed',
          status: 'error',
        });
      }
    } catch (error) {
      setTestResult({
        message: error instanceof Error ? error.message : 'Network error occurred',
        status: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="create-ingestion">
      <nav className="dashboard-nav">
        <div className="nav-container">
          <div className="logo">
            <span className="logo-icon">🐋</span>
            <span className="logo-text">Orca</span>
          </div>
          <div className="nav-links">
            <button className="btn-secondary" onClick={() => navigate('/app/dashboard')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </nav>

      <main className="create-ingestion-main">
        <div className="create-ingestion-container">
          <div className="create-ingestion-header">
            <h1>Create Ingestion</h1>
            <p className="create-ingestion-subtitle">Set up a new data source ingestion</p>
          </div>

          <div className="ingestion-form-section">
            <h2 className="section-title">Test Connection</h2>
            <p className="section-description">
              Test your ingestion setup before creating a full ingestion job.
            </p>

            <div className="test-section">
              <button
                className="btn-primary test-button"
                onClick={handleTestIngestion}
                disabled={isLoading}
              >
                {isLoading ? 'Testing...' : 'Test Ingestion'}
              </button>

              {testResult && (
                <div className={`test-result test-result-${testResult.status}`}>
                  <div className="test-result-header">
                    <span className="test-result-icon">
                      {testResult.status === 'success' ? '✅' : '❌'}
                    </span>
                    <span className="test-result-title">
                      {testResult.status === 'success' ? 'Success' : 'Failure'}
                    </span>
                  </div>
                  <div className="test-result-message">{testResult.message}</div>
                  {testResult.test_info && (
                    <div className="test-result-details">
                      <div className="test-detail-item">
                        <span className="test-detail-label">User:</span>
                        <span className="test-detail-value">{testResult.test_info.user}</span>
                      </div>
                      <div className="test-detail-item">
                        <span className="test-detail-label">Connection Test:</span>
                        <span className="test-detail-value">{testResult.test_info.connection_test}</span>
                      </div>
                      <div className="test-detail-item">
                        <span className="test-detail-label">Metadata Extraction:</span>
                        <span className="test-detail-value">{testResult.test_info.metadata_extraction}</span>
                      </div>
                      {testResult.timestamp && (
                        <div className="test-detail-item">
                          <span className="test-detail-label">Timestamp:</span>
                          <span className="test-detail-value">{testResult.timestamp}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CreateIngestion;

